import { Modal } from "@mantine/core";
import { useLocation } from "react-router";
import { useActiveCompanies } from "../../../../hooks/useActiveCompanies";
import { useContainerRef } from "../../../../hooks/useContainerRef";
import { useShouldFieldBeHighlighted } from "../../../../hooks/useShouldFieldBeHighlighted";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { Site } from "../../../../utils/backend-types";
import { useContract } from "../../../DeliveryInvoices/hooks/useContract";
import { useContractFormOptions } from "../../../DeliveryInvoices/hooks/useContractFormOptions";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { type Contract } from "../../DeliveryOverview.types";
import { CreateForm } from "./CreateForm/CreateForm";
import { EditForm } from "./EditForm/EditForm";

interface ContractModalProps {
  contractId?: string;
  projectId: string;
  variantId: number;
  withTemplate?: boolean;
  templateContracts?: Array<Contract>;
  sites?: Array<Site>;
  onSubmit: () => void;
  onClose: () => void;
}

function ContractModal({
  contractId,
  projectId,
  variantId,
  onSubmit,
  onClose,
  withTemplate,
  templateContracts,
  sites
}: ContractModalProps) {
  const { showToast } = useToast();
  const mode: "create" | "edit" = contractId ? "edit" : "create";
  const { containerNode, containerRef } = useContainerRef();
  const {
    data: contractFormOptions,
    isLoading: isContractFormOptionsLoading,
    error: contractFormOptionsError
  } = useContractFormOptions(projectId);
  const { data: activeCompanies } = useActiveCompanies(variantId, {
    enabled: withTemplate && mode === "create"
  });
  const {
    isContractDataLoading,
    contractLoadingError,
    contract,
    createContract,
    updateContract
  } = useContract(contractId);

  async function handleSubmitCreateContract(
    contract: Contract,
    projectId: string
  ) {
    try {
      await createContract({
        ...contract,
        payments: [],
        project: projectId
      });

      onSubmit();
      showToast({
        type: "success",
        message: "Vertrag wurde erfolgreich hinzugefügt"
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function handleSubmitEditContract(contract: Contract) {
    await updateContract(contract);
    onSubmit();
  }

  const editFormIsLoading =
    isContractDataLoading || isContractFormOptionsLoading;
  const editFormError = contractLoadingError || contractFormOptionsError;
  const { state: missingData } = useLocation();
  const missingFields =
    (missingData && missingData.missingFields.general) || [];
  const shouldFieldBeHighlighted = useShouldFieldBeHighlighted(missingFields);

  return (
    <Modal
      className="ContractModal"
      opened
      size="min(80vw, 1200px)"
      title={contractId ? "Vertrag bearbeiten" : "Vertrag hinzufügen"}
      onClose={onClose}
    >
      {contractId ? (
        <LoadOrError error={editFormError} loading={editFormIsLoading}>
          {contract && contractFormOptions && (
            <EditForm
              buttonContainer={containerNode}
              contract={contract}
              missingFields={missingFields}
              options={contractFormOptions}
              projectId={projectId}
              shouldFieldBeHighlighted={shouldFieldBeHighlighted}
              onCancel={onClose}
              onSubmit={handleSubmitEditContract}
            />
          )}
        </LoadOrError>
      ) : (
        <LoadOrError
          error={contractFormOptionsError}
          loading={isContractFormOptionsLoading}
        >
          {contractFormOptions && (
            <CreateForm
              activeCompanies={activeCompanies}
              buttonContainer={containerNode}
              missingFields={missingFields}
              options={contractFormOptions}
              shouldFieldBeHighlighted={shouldFieldBeHighlighted}
              sites={sites}
              templateContracts={templateContracts}
              withTemplate={withTemplate}
              onCancel={onClose}
              onSubmit={(contract) =>
                handleSubmitCreateContract(contract, projectId)
              }
            />
          )}
        </LoadOrError>
      )}
      <ModalFooter ref={containerRef} />
    </Modal>
  );
}

export { ContractModal, ContractModalProps };
